import styled from 'styled-components';
import { Input, Select, Textarea } from 'components/FormFields';
import { PlainButton } from 'components/v3';
import { ERROR_RED, phone } from '../palette';

// FormFields controls re-skinned for the phone fill. They already follow
// `theme.name` (the preview's dark/light); these only round the corners like
// the app and switch the error border to the design's red.
const invalidBorder = (p) => (p.$invalid ? `border-color: ${ERROR_RED};` : '');

export const FillInput = styled(Input)`
  && {
    border-radius: 8px;
    ${invalidBorder}
  }
`;

export const FillTextarea = styled(Textarea)`
  && {
    border-radius: 8px;
    min-height: 96px;
    resize: none;
    ${invalidBorder}
  }
`;

// Grey text while the placeholder option is showing, like the app.
export const FillSelect = styled(Select)`
  && {
    border-radius: 8px;
    ${(p) => (p.$empty ? `color: ${phone(p.theme).text3};` : '')}
    ${invalidBorder}
  }
`;

// Compact cell input for table grids and repeater entries.
export const CellInput = styled(Input)`
  && {
    padding: 9px 10px;
    font-size: 13px;
    border-radius: 7px;
    ${invalidBorder}
  }
`;

// Wraps the shared PhoneInput (country chip + number) so its number box
// rounds like the other fill inputs and turns red when invalid.
export const PhoneShell = styled.div`
  min-width: 0;

  input[type='tel'] {
    min-width: 0;
    border-radius: 8px;
    ${invalidBorder}
  }
`;

export const Stack = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${(p) => p.$gap || 8}px;
  min-width: 0;
`;

// Small pill buttons (email domain shortcuts).
export const ChipRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 8px;
`;

export const Chip = styled(PlainButton)`
  font-size: 12px;
  font-weight: 600;
  color: ${(p) => phone(p.theme).text2};
  background: ${(p) => phone(p.theme).surface};
  border: 1px solid ${(p) => phone(p.theme).border};
  border-radius: 16px;
  padding: 5px 11px;
  transition: border-color 150ms ease, color 150ms ease;

  &:hover {
    border-color: ${(p) => phone(p.theme).accent};
    color: ${(p) => phone(p.theme).accent};
  }
`;

export const Muted = styled.div`
  font-size: 12px;
  color: ${(p) => phone(p.theme).text3};
`;

// Props every single-control input spreads onto its element.
export const controlA11y = ({ inputId, describedBy, invalid, field }) => ({
  id: inputId,
  'aria-describedby': describedBy,
  'aria-invalid': invalid ? true : undefined,
  'aria-required': field && field.required ? true : undefined,
});

// Non-blank, de-duplicated option strings in definition order (a draft may
// still hold blank or repeated options; the server rejects those at Live).
export const uniqueOptions = (list) => {
  const seen = new Set();
  return (Array.isArray(list) ? list : []).filter((option) => {
    if (typeof option !== 'string' || !option.trim() || seen.has(option)) {
      return false;
    }
    seen.add(option);
    return true;
  });
};

export const listOf = (value) => (Array.isArray(value) ? value : []);

export const intIn = (value, min, max, fallback) =>
  Number.isInteger(value) ? Math.min(max, Math.max(min, value)) : fallback;
